import type { SignalColor, SignalItem, SignalSlot, TaskSignal } from '@indipia/shared'
import type { OrderVendorRow } from '../api/orderApi'
import {
  LIST_COLOR_RANK,
  type ListDisplayItem, type ListFilter, type ListFilterCounts, type ListMilestoneRow, type ListProgressStat,
  type ListSort, type ListSummary, type NextAction, type PeriodText, type ScheduleListRow,
} from '../types/scheduleList.types'
import type { GanttMilestone, ScheduleDetailDep, ScheduleDetailTask } from '../types/gantt.types'
import { countWorkdays } from '../views/scheduleUtils'
import { foremanStage, orderStage, workStage } from './scheduleListStage.logic'
import { SIGNAL_SLOTS, hasSignalAction, isSignalColor } from './signalView'

// 리스트 보기(sched-list) 집계·필터·정렬·대표 원장 행·기간·다음 할 일·마일스톤 순수 로직 (구현계획 B-05~B-12).
// 렌더·훅·API 없음. 신호 색·사유는 서버 값을 그대로 쓰고 여기서 날짜로 다시 판정하지 않는다.
// 날짜는 'YYYY-MM-DD'(로컬 달력일) 문자열, 오늘 값은 호출 쪽이 넘긴다(여기서 new Date() 를 쓰지 않는다).
// 단계(3칸) 판정은 scheduleListStage.logic.ts(B-01~04)가 맡고, 여기서는 행 모델을 만들 때만 가져다 쓴다.

// ── 날짜 보조 ──────────────────────────────────────────────────────────

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/

/** 'YYYY-MM-DD' → 로컬 Date(정오 기준이 아닌 자정). 형식이 틀리면 null(0·오늘로 대체하지 않음) */
function parseDay(v: string | null | undefined): Date | null {
  if (!v) return null
  const m = DAY_RE.exec(v)
  if (!m) return null
  const d = new Date(+m[1], +m[2] - 1, +m[3])
  return Number.isNaN(d.getTime()) ? null : d
}

/** a → b 달력일 차이(b 가 뒤면 양수) */
function dayDiff(a: Date, b: Date): number {
  return Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000)
}

function md(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`
}

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const

// ── B-05 대표 원장 행 선택 ──────────────────────────────────────────────

/**
 * 원장 상태 → 서버 판정(MockSignalJudgeSource.judgeOrder)이 그 행에서 만들 수 있는 발주 사유 코드.
 * 신호 응답에 원장 행 id 가 없어 FE 가 서버의 "가장 나쁜 하나"를 재현한다(계획서 6-7). 서버 규칙이 바뀌면 이 표와 시험을 함께 고친다.
 *  DRAFT = 날짜 후보(발주일 도래전·발주정보요청대기·미발주), REPLACED(거래처 교체됨)는 서버가 후보에서 제외.
 *  CONFIRMED·SHIP_WAIT·SHIP_REPLIED = judgeShipOrReceipt(입고 지연 → 오늘의 배송(SHIP_REPLIED만) → 입고등록대기 → 배송정보 대기(SHIP_WAIT만) → 발주 확정).
 *  RECEIVED = 입고 완료, 입고 이슈 조치 전이면 입고 이슈.
 */
export const ORDER_STATUS_REASON_COMPAT: Record<string, readonly string[]> = {
  DRAFT: ['ORDER_BEFORE_DATE', 'ORDER_SEND_DUE', 'ORDER_SEND_OVERDUE'],
  REPLY_WAIT: ['ORDER_REPLY_WAIT'],
  REREPLY_WAIT: ['ORDER_REREPLY_WAIT'],
  RESEND_NEEDED: ['ORDER_RESEND_NEEDED'],
  DECLINED: ['ORDER_REASSIGN_NEEDED'],
  CONFIRMED: ['ORDER_RECEIPT_DELAY', 'ORDER_RECEIPT_WAIT', 'ORDER_CONFIRMED'],
  SHIP_WAIT: ['ORDER_RECEIPT_DELAY', 'ORDER_RECEIPT_WAIT', 'ORDER_SHIP_INFO_WAIT'],
  SHIP_REPLIED: ['ORDER_RECEIPT_DELAY', 'ORDER_TODAY_DELIVERY', 'ORDER_RECEIPT_WAIT', 'ORDER_CONFIRMED'],
  RECEIVED: ['ORDER_RECEIVED', 'ORDER_RECEIPT_ISSUE'],
}

const ORDER_DATE_REASONS: readonly string[] = ORDER_STATUS_REASON_COMPAT.DRAFT

/** 대표 행 선택에 쓰는 원장 행 필드(OrderVendorRow 의 일부) */
export type VendorRowLike = Pick<OrderVendorRow, 'orderVendorId' | 'orderStatusCode'> & {
  delivery?: Pick<NonNullable<OrderVendorRow['delivery']>, 'arrivalDate'> | null
  receipt?: Pick<NonNullable<OrderVendorRow['receipt']>, 'qtyCheckedCode'> | null
}

function rowCompatible(row: VendorRowLike, reasonCode: string, today?: string): boolean {
  const allowed = ORDER_STATUS_REASON_COMPAT[row.orderStatusCode]
  if (!allowed || !allowed.includes(reasonCode)) return false
  // 같은 사유를 만들 수 있는 행이 여럿일 때, 행 자료로 확인되는 조건만 좁힌다
  if (reasonCode === 'ORDER_TODAY_DELIVERY' && today && row.delivery?.arrivalDate) {
    return row.delivery.arrivalDate.slice(0, 10) === today
  }
  if (reasonCode === 'ORDER_RECEIPT_ISSUE' && row.receipt) {
    return row.receipt.qtyCheckedCode === 'ISSUE'
  }
  return true
}

/**
 * 발주 슬롯 신호의 사유 코드와 호환되는 원장 행 중 id 가 가장 작은 행 = 서버가 고른 "가장 나쁜 하나".
 * 반환 null = 대표 행 없음: 날짜 후보 판정(DRAFT 행·행 없음)이지만 DRAFT 행도 없는 경우 · ORDER_NA · 모르는 사유 코드 · 호환 행 없음.
 * 날짜 후보 사유(발주일 도래전·발주정보요청대기·미발주)는 DRAFT 행이 있으면 그 중 id 최소 행을 돌려준다.
 * REPLACED(거래처 교체됨)는 서버가 후보에서 제외하므로 여기서도 제외한다.
 */
export function pickRepresentativeVendorRow<T extends VendorRowLike>(
  rows: readonly T[], orderReasonCode: string | null | undefined, today?: string,
): T | null {
  if (!orderReasonCode || orderReasonCode === 'ORDER_NA') return null
  let best: T | null = null
  for (const row of rows) {
    if (row.orderStatusCode === 'REPLACED') continue
    if (!rowCompatible(row, orderReasonCode, today)) continue
    if (best == null || row.orderVendorId < best.orderVendorId) best = row
  }
  return best
}

/** 이 사유가 날짜 후보(원장 행 없이도 나오는 판정)인지 */
export function isOrderDateReason(reasonCode: string): boolean {
  return ORDER_DATE_REASONS.includes(reasonCode)
}

// ── B-12 기간 칸 ────────────────────────────────────────────────────────

/**
 * 기간 칸 문구. 일수 n = 서버 작업일 `duration`(계획서 6-6: 간트 소요일과 같은 작업일 기준).
 *  종료일 < 오늘 → 'n일 · 끝남' / 착공 ≤ 오늘 → '진행중 (경과)/n일' / 그 외 → 'n일 · 착공 D-k'(k = 착공 − 오늘, 달력일).
 * 경과 = 착공~오늘의 작업일 수(휴일 제외, 최대 n). holidays 가 null 이면(휴일 조회 전·실패) 경과를 만들지 않고 '진행중 · n일'만 표시한다.
 * 날짜 형식이 틀리면 null(날짜를 만들어 채우지 않음).
 */
export function periodText(
  task: Pick<ScheduleDetailTask, 'startDate' | 'endDate' | 'duration'>, today: string, holidays: Set<string> | null,
): PeriodText | null {
  const start = parseDay(task.startDate)
  const end = parseDay(task.endDate)
  const now = parseDay(today)
  if (!start || !end || !now) return null
  const days = task.duration
  const range = `${md(start)} ~ ${md(end)}`
  if (dayDiff(end, now) > 0) return { range, status: `${days}일 · 끝남`, state: 'ended', days }
  if (dayDiff(start, now) >= 0) {
    if (holidays == null) return { range, status: `진행중 · ${days}일`, state: 'running', days }
    const elapsed = Math.min(countWorkdays(start, now, holidays), days)
    return { range, status: `진행중 ${elapsed}/${days}일`, state: 'running', days }
  }
  return { range, status: `${days}일 · 착공 D-${dayDiff(now, start)}`, state: 'upcoming', days }
}

// ── B-10 다음 할 일 ─────────────────────────────────────────────────────

function nextActionOf(slot: SignalSlot, item: SignalItem | undefined): NextAction | null {
  if (!item) return null
  if (item.colorCode !== 'RED' && item.colorCode !== 'YELLOW') return null
  // 서버가 버튼 문구와 주소를 내려준 신호만 버튼이 된다(리마인드·노랑 4종은 서버가 문구를 비워 보냄 → 제외)
  if (!hasSignalAction(item) || !item.actionName) return null
  return {
    slot, color: item.colorCode, reasonCode: item.reasonCode,
    action: { actionName: item.actionName, actionTypeCode: item.actionTypeCode, webPath: item.webPath, actionUrl: item.actionUrl },
    buttonLabel: item.actionName,
  }
}

/**
 * 다음 할 일 — 반장 → 발주 → 작업 순으로 빨강·노랑이면서 처리 버튼이 있는 것 중 심각도가 가장 높은 하나.
 * 같은 색이면 앞 순서. 흰색·녹색·파랑·해당 없음과 버튼 없는 상태(리마인드 포함)는 후보가 아니다. 없으면 null('—').
 */
export function pickNextAction(signals: Record<SignalSlot, SignalItem | undefined>): NextAction | null {
  let best: NextAction | null = null
  for (const slot of SIGNAL_SLOTS) {
    const cand = nextActionOf(slot, signals[slot])
    if (!cand) continue
    if (best == null || (cand.color === 'RED' && best.color === 'YELLOW')) best = cand
  }
  return best
}

// ── B-07 요약줄 ─────────────────────────────────────────────────────────

function stat(done: number, total: number): ListProgressStat {
  return { done, total, percent: total === 0 ? 0 : Math.round((done / total) * 100) }
}

/**
 * 요약줄 집계 — 필터와 무관, 현장 전체 공종 기준.
 *  반장 일정 확정 = FOREMAN_CONFIRMED / FOREMAN_NA(자재시공)가 아닌 공종, 발주·입고 완료 = ORDER_RECEIVED / ORDER_NA 가 아닌 공종('거래처 미배정'도 포함),
 *  작업 검수완료 = WORK_DONE / 전체 공종, 조치 필요·대기 = 행 대표색 빨강·노랑 공종 수.
 * 이 공종의 발주 신호가 없으면(신호 없음) ORDER_NA 가 아니므로 발주 분모에 들어간다(해당 없음이라고 단정하지 않음).
 */
export function buildListSummary(
  rows: readonly Pick<ScheduleListRow, 'signals' | 'representativeColor'>[], milestoneCount: number,
): ListSummary {
  let foremanDone = 0
  let foremanTotal = 0
  let orderDone = 0
  let orderTotal = 0
  let workDone = 0
  let red = 0
  let yellow = 0
  for (const r of rows) {
    const foreman = r.signals.FOREMAN?.reasonCode
    if (foreman !== 'FOREMAN_NA') {
      foremanTotal++
      if (foreman === 'FOREMAN_CONFIRMED') foremanDone++
    }
    const order = r.signals.ORDER?.reasonCode
    if (order !== 'ORDER_NA') {
      orderTotal++
      if (order === 'ORDER_RECEIVED') orderDone++
    }
    if (r.signals.WORK?.reasonCode === 'WORK_DONE') workDone++
    if (r.representativeColor === 'RED') red++
    else if (r.representativeColor === 'YELLOW') yellow++
  }
  return {
    foreman: stat(foremanDone, foremanTotal),
    order: stat(orderDone, orderTotal),
    work: stat(workDone, rows.length),
    redCount: red,
    yellowCount: yellow,
    taskCount: rows.length,
    milestoneCount,
  }
}

// ── B-08 필터 ───────────────────────────────────────────────────────────

/** 이번주 착공 범위 [일요일, 토요일] 'YYYY-MM-DD'. today 형식이 틀리면 null */
export function weekRange(today: string): { from: string; to: string } | null {
  const now = parseDay(today)
  if (!now) return null
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay())
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6)
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { from: iso(from), to: iso(to) }
}

type FilterRow = Pick<ScheduleListRow, 'signals' | 'representativeColor' | 'startDate'>

/** 칩 판정식(L1 §4-1). 이번주 착공은 착공일만 비교(기간 겹침 아님) */
export function matchesFilter(row: FilterRow, filter: ListFilter, today: string): boolean {
  switch (filter) {
    case '':
      return true
    case 'ry':
      return row.representativeColor === 'RED' || row.representativeColor === 'YELLOW'
    case 'p': {
      const foreman = row.signals.FOREMAN?.reasonCode
      return foreman !== 'FOREMAN_NA' && foreman !== 'FOREMAN_CONFIRMED'
    }
    case 'o': {
      const order = row.signals.ORDER?.reasonCode
      return order !== 'ORDER_NA' && order !== 'ORDER_RECEIVED'
    }
    case 'w':
      return row.signals.WORK?.reasonCode !== 'WORK_DONE'
    case 'wk': {
      const week = weekRange(today)
      const start = row.startDate.slice(0, 10)
      return week != null && start >= week.from && start <= week.to
    }
  }
}

/** 칩별 건수 — 현재 행 전체 기준(다른 칩 선택과 무관) */
export function countFilters(rows: readonly FilterRow[], today: string): ListFilterCounts {
  const counts: ListFilterCounts = { '': rows.length, ry: 0, p: 0, o: 0, w: 0, wk: 0 }
  for (const r of rows) {
    if (matchesFilter(r, 'ry', today)) counts.ry++
    if (matchesFilter(r, 'p', today)) counts.p++
    if (matchesFilter(r, 'o', today)) counts.o++
    if (matchesFilter(r, 'w', today)) counts.w++
    if (matchesFilter(r, 'wk', today)) counts.wk++
  }
  return counts
}

export function filterRows<T extends FilterRow>(rows: readonly T[], filter: ListFilter, today: string): T[] {
  return rows.filter(r => matchesFilter(r, filter, today))
}

// ── B-09 정렬 ───────────────────────────────────────────────────────────

type SortRow = Pick<ScheduleListRow, 'taskId' | 'startDate' | 'representativeColor'>

function byDateThenId(a: SortRow, b: SortRow): number {
  if (a.startDate !== b.startDate) return a.startDate < b.startDate ? -1 : 1
  return a.taskId - b.taskId
}

/**
 * 일정순 = 착공일 오름차순 / 급한순 = 대표색 심각도(빨강<노랑<파랑<녹색<흰색) 후 착공일.
 * 마지막 기준은 먼저 등록한 공종(taskId 오름차순, 확정 방침 — 신본 규정 없음). 입력 배열은 바꾸지 않는다.
 */
export function sortRows<T extends SortRow>(rows: readonly T[], sort: ListSort): T[] {
  const copy = [...rows]
  if (sort === 'urgent') {
    copy.sort((a, b) => {
      const ra = LIST_COLOR_RANK[a.representativeColor as SignalColor]
      const rb = LIST_COLOR_RANK[b.representativeColor as SignalColor]
      return ra !== rb ? ra - rb : byDateThenId(a, b)
    })
  } else {
    copy.sort(byDateThenId)
  }
  return copy
}

// ── B-11 마일스톤 행 ────────────────────────────────────────────────────

/** 'D-n'(양수) · 'D-day'(0) · 'D+n'(음수) */
export function ddayText(dday: number): string {
  if (dday === 0) return 'D-day'
  return dday > 0 ? `D-${dday}` : `D+${-dday}`
}

/** 마일스톤 표시 문구 '◆ 이름 · M/D(요일) · D-n' 에서 '◆' 뒤 부분 — 이름 · M/D(요일) · D-n */
export function milestoneLabel(m: Pick<ListMilestoneRow, 'milestoneName' | 'date' | 'ddayText'>): string {
  const d = parseDay(m.date)
  const when = d ? `${md(d)}(${WEEKDAY_KO[d.getDay()]})` : m.date
  return `${m.milestoneName} · ${when} · ${m.ddayText}`
}

/** 서버 마일스톤 → 표시 행. 날짜 형식이 틀리면 null */
export function toMilestoneRow(m: GanttMilestone, today: string): ListMilestoneRow | null {
  const date = parseDay(m.milestoneDate)
  const now = parseDay(today)
  if (!date || !now) return null
  const dday = dayDiff(now, date)
  return {
    milestoneId: m.milestoneId,
    milestoneType: m.milestoneType,
    milestoneName: m.milestoneName,
    date: m.milestoneDate.slice(0, 10),
    dday,
    ddayText: ddayText(dday),
  }
}

/**
 * 공종 행에 마일스톤 행을 끼워 넣는다. 일정순 + 전체 필터일 때만 표시(그 밖에는 공종 행만).
 * 마일스톤 날짜 ≤ 공종 착공일이면 그 공종 앞에 삽입, 남은 것은 맨 끝. rows 는 이미 정렬·필터된 목록.
 */
export function mergeMilestones(
  rows: readonly ScheduleListRow[], milestones: readonly GanttMilestone[], sort: ListSort, filter: ListFilter, today: string,
): ListDisplayItem[] {
  const taskItems = (): ListDisplayItem[] => rows.map(row => ({ kind: 'task', row }))
  if (sort !== 'date' || filter !== '') return taskItems()
  const pending = milestones
    .map(m => toMilestoneRow(m, today))
    .filter((m): m is ListMilestoneRow => m != null)
    .sort((a, b) => (a.date !== b.date ? (a.date < b.date ? -1 : 1) : a.milestoneId - b.milestoneId))
  const out: ListDisplayItem[] = []
  let i = 0
  for (const row of rows) {
    while (i < pending.length && pending[i].date <= row.startDate.slice(0, 10)) {
      out.push({ kind: 'milestone', milestone: pending[i++] })
    }
    out.push({ kind: 'task', row })
  }
  while (i < pending.length) out.push({ kind: 'milestone', milestone: pending[i++] })
  return out
}

/** 신호 응답에서 슬롯별 항목 3개를 뽑는다(없으면 undefined, 대체값 없음) */
export function signalsBySlot(ts: TaskSignal | undefined): Record<SignalSlot, SignalItem | undefined> {
  return {
    FOREMAN: ts?.signals.find(s => s.signalTypeCode === 'FOREMAN'),
    ORDER: ts?.signals.find(s => s.signalTypeCode === 'ORDER'),
    WORK: ts?.signals.find(s => s.signalTypeCode === 'WORK'),
  }
}

// ── B-06 행 모델 ────────────────────────────────────────────────────────

export interface BuildListRowsInput {
  tasks: readonly ScheduleDetailTask[]
  deps: readonly ScheduleDetailDep[]
  /** GET /schedule/signals 응답 items. 응답에 없는 공종은 '신호 없음' 행이 된다 */
  signals: readonly TaskSignal[]
  /** 이 일정의 발주 원장 행 전체(REPLACED 는 서버 후보 제외라 여기서도 제외) */
  vendorRows: readonly OrderVendorRow[]
  /** 'YYYY-MM-DD' 로컬 달력일 */
  today: string
  /** 휴일 날짜 집합. 없으면(null·생략) 기간 칸에 경과 일수를 만들지 않는다(진행중 · n일) */
  holidays?: Set<string> | null
  /** 스케줄 전용 행정 공정 공종코드(scheduleOnlyYn='Y') — 신호를 보지 않고 3칸을 '해당 없음'으로 둔다 */
  scheduleOnlyCodes?: ReadonlySet<string>
}

/**
 * 공종 1건 → 행 1건(taskId 기준, 입력 순서 유지). 정렬·필터는 sortRows·filterRows 가 따로 맡는다.
 *  행 대표색 = 서버 representativeColorCode(null 이면 흰색, '셋 다 흰색 숨김'은 적용하지 않음),
 *  3칸 = foremanStage·orderStage·workStage(B-01~03), 선행 = 같은 일정 공종 이름(없으면 빈 배열 → '선행 없음'),
 *  신호가 없는 공종은 signalMissing = true 이고 3칸이 'no-signal' 이다.
 */
export function buildScheduleListRows(input: BuildListRowsInput): ScheduleListRow[] {
  const { tasks, deps, signals, vendorRows, today } = input
  const holidays = input.holidays ?? null
  const signalByTask = new Map<number, TaskSignal>()
  for (const s of signals) signalByTask.set(s.taskId, s)
  const taskById = new Map<number, ScheduleDetailTask>()
  for (const t of tasks) taskById.set(t.taskId, t)
  const rowsByTask = new Map<number, OrderVendorRow[]>()
  for (const r of vendorRows) {
    if (r.orderStatusCode === 'REPLACED') continue
    const list = rowsByTask.get(r.taskId)
    if (list) list.push(r)
    else rowsByTask.set(r.taskId, [r])
  }

  return tasks.map((task): ScheduleListRow => {
    const scheduleOnly = input.scheduleOnlyCodes?.has(task.workTypeCode) ?? false
    // 스케줄 전용 행정 공정은 시공·자재가 없어 신호를 쓰지 않는다(간트·캘린더와 같은 기준)
    const ts = scheduleOnly ? undefined : signalByTask.get(task.taskId)
    const sig = signalsBySlot(ts)
    const taskVendorRows = rowsByTask.get(task.taskId) ?? []
    const direct = !scheduleOnly && sig.ORDER?.reasonCode !== 'ORDER_NA'
    const hasVendor = direct && taskVendorRows.length > 0
    let vendorName: string | null = null
    if (hasVendor) {
      const rep = pickRepresentativeVendorRow(taskVendorRows, sig.ORDER?.reasonCode, today)
        ?? taskVendorRows.reduce((a, b) => (b.orderVendorId < a.orderVendorId ? b : a))
      vendorName = rep.vendorName
    }

    const predecessors = deps
      .filter(d => d.successorTaskId === task.taskId)
      .map(d => taskById.get(d.predecessorTaskId))
      .filter((p): p is ScheduleDetailTask => p != null)
      .sort((a, b) => a.displayOrder - b.displayOrder || a.taskId - b.taskId)
      .map(p => ({ taskId: p.taskId, workTypeName: p.workTypeName }))

    const repColor = ts?.representativeColorCode
    return {
      taskId: task.taskId,
      workTypeCode: task.workTypeCode,
      workTypeName: task.workTypeName,
      startDate: task.startDate,
      endDate: task.endDate,
      duration: task.duration,
      predecessors,
      signals: sig,
      signalMissing: !scheduleOnly && ts == null,
      representativeColor: repColor && isSignalColor(repColor) ? repColor : 'WHITE',
      cells: scheduleOnly
        ? { foreman: { status: 'none', slot: 'SCHEDULE_ONLY' }, order: { status: 'none', slot: 'SCHEDULE_ONLY' }, work: { status: 'none', slot: 'SCHEDULE_ONLY' } }
        : {
            foreman: foremanStage(sig.FOREMAN),
            order: orderStage(sig.ORDER, { hasVendor }),
            work: workStage(sig.WORK),
          },
      nextAction: scheduleOnly ? null : pickNextAction(sig),
      period: periodText(task, today, holidays) ?? { range: '-', status: '-', state: 'upcoming', days: task.duration },
      foremanName: task.foremanName ? task.foremanName : null,
      vendorName,
      hasVendor,
      scheduleOnly,
    }
  })
}
